<template>
  <vue-date-picker
    :model-value="selectedDate"
    :uid="'date-picker'"
    :locale="globalContent.getLocale"
    :format="format"
    :preview-format="format"
    :min-date="minDate"
    :enable-time-picker="false"
    :clearable="false"
    :disabled-week-days="disabledWeekDays"
    :disabled-dates="disabledDates"
    :space-confirm="false"
    :position="'left'"
    hide-offset-dates
    cancel-text="Close"
    :class="{
      '--vdp-bg-color': '#ffffff',
      '--vdp-text-color': '#000000',
      '--vdp-box-shadow': '0 4px 10px 0 rgba(128, 144, 160, 0.1), 0 0 1px 0 rgba(128, 144, 160, 0.81)',
      '--vdp-border-radius': '0px',
      '--vdp-heading-size': '2.5em',
      '--vdp-heading-weight': 'bold',
      '--vdp-heading-hover-color': '#eeeeee',
      '--vdp-selected-color': '#e6f2f5',
      '--vdp-elem-border-radius': '0px',
      '--dp-icon-color': '#2C4D57',
    }"
    @update:model-value="onSelectDate"
  >
    <template #input-icon>
      <img
        src="/icons/calendar.svg"
        class="w-16 h-16 text-text-attention"
        alt=""
      >
    </template>
  </vue-date-picker>
</template>

<script setup lang="ts">
import VueDatePicker from '@vuepic/vue-datepicker';
import { ref } from 'vue';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';
import '@vuepic/vue-datepicker/dist/main.css';
import { NorceInfoTypesCodes } from '~/constants/norceCodes';

const globalContent = useGlobalContentStore();
const userStore = useUserStore();

const props = defineProps<{
  partNo: string,
  date: string,
  defaultDate: string,
}>();

const emit = defineEmits<{
  (e: 'updatePreferredShippingDate', date: string): void;
}>();

const parseLocalDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if ([year, month, day].some((part) => Number.isNaN(part))) return null;

  return new Date(year, month - 1, day);
};

const selectedDate = ref<Date | null>(parseLocalDate(props.date));
const minDate = ref<Date | null>(parseLocalDate(props.defaultDate));

watch(
  () => props.date,
  (value) => {
    selectedDate.value = parseLocalDate(value);
  }
);

watch(
  () => props.defaultDate,
  (value) => {
    minDate.value = parseLocalDate(value);
  }
);
const format = (date: Date) => {
  let day = date.getDate();
  if (day < 10) {
    day = '0'+ day.toString();
  }
  let month = date.getMonth() + 1;
  if (month < 10) {
    month = '0'+ month.toString();
  }
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

const disabledWeekDays = computed(() => {
  if (userStore.userProfile.company.mod !== '') {
    const allWeekdays = [0,1,2,3,4,5,6];
    const matches = [...userStore.userProfile.company.mod.matchAll(/[1-7]/g)].map((i) => Number(i[0]));
    return allWeekdays.filter(x => !matches.includes(x));
  }

  return [6,0]; // default -> allow mon-fri delivery
});

function getSwedishRedDays(year: number = new Date().getFullYear()): Date[] {
  const d = (month: number, day: number) => new Date(year, month - 1, day);

  function getEasterDate(y: number): Date {
    const f = Math.floor;
    const G = y % 19;
    const C = f(y / 100);
    const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
    const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
    const J = (y + f(y / 4) + I + 2 - C + f(C / 4)) % 7;
    const L = I - J;
    const month = 3 + f((L + 40) / 44);
    const day = L + 28 - 31 * f(month / 4);
    return new Date(y, month - 1, day);
  }

  function getMidsummerDay(): Date {
    const startDate = new Date(year, 5, 20); // June 20
    for (let i = 0; i <= 6; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      if (date.getDay() === 6) return date;
    }
    throw new Error('Midsummer Day not found');
  }

  function getAllSaintsDay(): Date {
    const startDate = new Date(year, 9, 31); // October 31
    for (let i = 0; i <= 6; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      if (date.getDay() === 6) return date;
    }
    throw new Error('All Saints Day not found');
  }

  const easter = getEasterDate(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);

  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);

  const ascensionDay = new Date(easter);
  ascensionDay.setDate(easter.getDate() + 39);

  const pentecost = new Date(easter);
  pentecost.setDate(easter.getDate() + 49);

  return [
    d(1, 1),         // New Year's Day
    d(1, 6),         // Epiphany
    goodFriday,      // Good Friday
    easter,          // Easter Sunday
    easterMonday,    // Easter Monday
    d(5, 1),         // Labour Day
    ascensionDay,    // Ascension Day
    pentecost,       // Pentecost
    d(6, 6),         // National Day
    getMidsummerDay(), // Midsummer Day (Saturday)
    getAllSaintsDay(), // All Saints' Day (Saturday)
    d(12, 24),       // Christmas Eve (semi-official)
    d(12, 25),       // Christmas Day
    d(12, 26),       // Boxing Day
    d(12, 31),       // New Year's Eve (semi-official)
  ];
}

function getUSHolidays(year: number = new Date().getFullYear()): Date[] {
  const d = (month: number, day: number) => new Date(year, month - 1, day);

  function getEasterDate(y: number): Date {
    const f = Math.floor;
    const G = y % 19;
    const C = f(y / 100);
    const H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30;
    const I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11));
    const J = (y + f(y / 4) + I + 2 - C + f(C / 4)) % 7;
    const L = I - J;
    const month = 3 + f((L + 40) / 44);
    const day = L + 28 - 31 * f(month / 4);
    return new Date(y, month - 1, day);
  }

  // Helper to get nth weekday of month (e.g., 3rd Monday)
  function getNthWeekdayOfMonth(month: number, weekday: number, n: number): Date {
    const firstDay = new Date(year, month - 1, 1);
    const firstWeekday = firstDay.getDay();
    let diff = weekday - firstWeekday;
    if (diff < 0) diff += 7;
    const day = 1 + diff + (n - 1) * 7;
    return new Date(year, month - 1, day);
  }

  // Helper to get last weekday of month
  function getLastWeekdayOfMonth(month: number, weekday: number): Date {
    const lastDay = new Date(year, month, 0); // Last day of month
    const lastWeekday = lastDay.getDay();
    let diff = lastWeekday - weekday;
    if (diff < 0) diff += 7;
    const day = lastDay.getDate() - diff;
    return new Date(year, month - 1, day);
  }

  const holidays: Date[] = [];

  // New Year's Day + adjacent day based on day of week
  const newYearsDay = d(1, 1);
  holidays.push(newYearsDay);
  const newYearsDayOfWeek = newYearsDay.getDay();
  if (newYearsDayOfWeek === 0) { // Sunday
    holidays.push(d(1, 2)); // Monday after
  } else if (newYearsDayOfWeek === 6) { // Saturday
    holidays.push(d(12, 31)); // Friday before (previous year)
  } else if (newYearsDayOfWeek === 2) { // Tuesday - day before is Monday
    const mondayBefore = new Date(newYearsDay);
    mondayBefore.setDate(newYearsDay.getDate() - 1);
    holidays.push(mondayBefore); // December 31 (Monday)
  } else if (newYearsDayOfWeek === 4) { // Thursday
    holidays.push(d(1, 2)); // Friday after
  }

  // MLK Day - 3rd Monday in January
  holidays.push(getNthWeekdayOfMonth(1, 1, 3));

  // President's Day - 3rd Monday in February
  holidays.push(getNthWeekdayOfMonth(2, 1, 3));

  // Good Friday - 2 days before Easter
  const easter = getEasterDate(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  holidays.push(goodFriday);

  // Memorial Day - Last Monday in May
  holidays.push(getLastWeekdayOfMonth(5, 1));

  // Juneteenth - June 19
  holidays.push(d(6, 19));

  // Independence Day - July 4
  holidays.push(d(7, 4));

  // Labor Day - 1st Monday in September
  holidays.push(getNthWeekdayOfMonth(9, 1, 1));

  // Thanksgiving Day - 4th Thursday in November
  holidays.push(getNthWeekdayOfMonth(11, 4, 4));

  // Day after Thanksgiving - Friday after 4th Thursday
  const thanksgiving = getNthWeekdayOfMonth(11, 4, 4);
  const dayAfterThanksgiving = new Date(thanksgiving);
  dayAfterThanksgiving.setDate(thanksgiving.getDate() + 1);
  holidays.push(dayAfterThanksgiving);

  // Christmas Day + adjacent day based on day of week
  const christmasDay = d(12, 25);
  holidays.push(christmasDay);
  const christmasDayOfWeek = christmasDay.getDay();
  if (christmasDayOfWeek === 0) { // Sunday
    holidays.push(d(12, 26)); // Monday after
  } else if (christmasDayOfWeek === 6) { // Saturday
    holidays.push(d(12, 24)); // Friday before
  } else if (christmasDayOfWeek === 2) { // Tuesday - day before is Monday
    holidays.push(d(12, 24)); // Monday before
  } else if (christmasDayOfWeek === 4) { // Thursday
    holidays.push(d(12, 26)); // Friday after
  }

  return holidays;
}

// Select appropriate holiday function based on country
const disabledDates = computed(() => {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  if (globalContent.getCountryCode === 'us') {
    // Include both current and next year holidays
    const holidays = [...getUSHolidays(currentYear), ...getUSHolidays(nextYear)];
    return holidays;
  }
  return [...getSwedishRedDays(currentYear), ...getSwedishRedDays(nextYear)];
});

const onSelectDate = (modelData: Date | null) => {
  selectedDate.value = modelData;

  if (selectedDate.value) {
    emit('updatePreferredShippingDate', format(selectedDate.value));
  }
};

</script>

<style>
#dp-input-date-picker {
  @apply border-none pl-24 py-0 underline text-text-attentionStrong text-12 desk:text-14 font-karla font-semibold text-pretty;
}
</style>
